import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Plus, Pencil, Trash2, Check, Info, X } from "lucide-react";
import { DashboardShell, PageHead, EmptyState, ErrorState } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { journeyApi, apiError } from "@/lib/journeyApi";
import { useAsyncData } from "@/lib/useAsyncData";
import { fmt } from "@/lib/format";
import { track } from "@/lib/analytics";

const DRAFT_KEY = "ms_money_landed_draft";

const SOURCES = [
  { key: "commission", label: "Commission" },
  { key: "bonus", label: "Bonus" },
  { key: "freelance", label: "Freelance / invoice" },
  { key: "overtime", label: "Overtime" },
  { key: "side_income", label: "Side income" },
  { key: "gift", label: "Gift" },
  { key: "tax_refund", label: "Tax refund" },
  { key: "other", label: "Other" },
];
const SOURCE_LABEL = Object.fromEntries(SOURCES.map((s) => [s.key, s.label]));

const REMINDERS = [
  { key: "on_date", label: "On the expected date" },
  { key: "3", label: "3 days after" },
  { key: "7", label: "7 days after" },
  { key: "14", label: "14 days after" },
  { key: "custom", label: "Pick a date" },
  { key: "none", label: "No reminder" },
];

const inputCls =
  "mt-1 min-h-11 w-full rounded-xl pe-surface px-3.5 text-[#171717] outline-none focus:border-[#2F6B57]";
const labelCls = "text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]";

function prettyDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

const emptyForm = () => ({
  label: "",
  amount: "",
  expected_date: new Date().toISOString().slice(0, 10),
  source: "commission",
  reminder: "on_date",
  reminder_date: "",
  note: "",
});

function EventForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial || emptyForm());
  const [busy, setBusy] = useState(false);
  const editing = Boolean(initial?.id);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.label.trim()) return toast.error("Give this expected income a name.");
    if (!form.expected_date) return toast.error("Choose when you expect it.");
    if (form.reminder === "custom" && !form.reminder_date) return toast.error("Pick a reminder date.");
    setBusy(true);
    try {
      const payload = {
        label: form.label.trim(),
        amount: form.amount ? Number(form.amount) : 0,
        expected_date: form.expected_date,
        source: form.source,
        reminder: form.reminder,
        reminder_date: form.reminder === "custom" ? form.reminder_date : null,
        note: form.note || null,
      };
      if (editing) await journeyApi.updateExpectedIncome(initial.id, payload);
      else await journeyApi.createExpectedIncome(payload);
      if (!editing) track("expected_income_added", { source: form.source, reminder: form.reminder });
      toast.success(editing ? "Expected income updated." : "Expected income added.");
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Could not save."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="rounded-2xl pe-surface p-5" data-testid="expected-income-form">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelCls}>What is it?</label>
          <input value={form.label} onChange={set("label")} placeholder="Q3 commission" data-testid="expected-label" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Expected amount (optional)</label>
          <input value={form.amount} inputMode="decimal" onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="$" data-testid="expected-amount" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>When do you expect it?</label>
          <input type="date" value={form.expected_date} onChange={set("expected_date")} data-testid="expected-date" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Type</label>
          <select value={form.source} onChange={set("source")} data-testid="expected-source" className={inputCls}>
            {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Remind me to check</label>
          <select value={form.reminder} onChange={set("reminder")} data-testid="expected-reminder" className={inputCls}>
            {REMINDERS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </div>
        {form.reminder === "custom" && (
          <div className="sm:col-span-2">
            <label className={labelCls}>Reminder date</label>
            <input type="date" value={form.reminder_date} onChange={set("reminder_date")} data-testid="expected-reminder-date" className={inputCls} />
          </div>
        )}
        <div className="sm:col-span-2">
          <label className={labelCls}>Note (optional)</label>
          <input value={form.note} onChange={set("note")} placeholder="Anything to remember" data-testid="expected-note" className={inputCls} />
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Button disabled={busy} type="submit" data-testid="expected-save" className="min-h-11 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643] sm:w-auto sm:px-6">
          {editing ? "Save changes" : "Add expected income"}
        </Button>
        <Button variant="ghost" type="button" disabled={busy} onClick={onCancel} data-testid="expected-cancel" className="min-h-11 w-full rounded-xl text-[#70584A] hover:bg-[#F0EBE2] sm:w-auto sm:px-6">Cancel</Button>
      </div>
    </form>
  );
}

function EventCard({ e, onChanged }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const markReceived = async () => {
    setBusy(true);
    try {
      const res = await journeyApi.receivedExpectedIncome(e.id);
      track("expected_income_received", { source: e.source });
      // Planning only: prefill the Money Landed draft as a HINT. The customer
      // still confirms the real amount in the flow (never auto-converted).
      try {
        const draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "{}");
        draft.actual = res.suggested_amount ? String(res.suggested_amount) : "";
        draft.landedDate = new Date().toISOString().slice(0, 10);
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch {}
      toast.success("Nice. Enter what actually landed.");
      navigate("/dashboard/money-landed");
    } catch (err) {
      toast.error(apiError(err, "Could not update."));
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await journeyApi.dismissExpectedIncome(e.id);
      toast.success("Removed from your plan.");
      onChanged();
    } catch (err) {
      toast.error(apiError(err, "Could not remove."));
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <EventForm
        initial={{ ...e, amount: e.amount ? String(e.amount) : "", reminder_date: e.reminder_date || "", note: e.note || "" }}
        onCancel={() => setEditing(false)}
        onSaved={() => { setEditing(false); onChanged(); }}
      />
    );
  }

  return (
    <div className="rounded-2xl pe-surface p-5" data-testid={`expected-card-${e.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-serif text-xl text-[#171717]">{e.label}</p>
          <p className="mt-0.5 text-sm text-[#70584A]">
            {e.amount > 0 ? `${fmt(e.amount)} expected · ` : ""}{SOURCE_LABEL[e.source] || "Other"}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-[#8A7C6B]">
            <CalendarClock className="h-4 w-4 text-[#C9A86A]" /> Around {prettyDate(e.expected_date)}
          </p>
          {e.reminder !== "none" && e.checkin_date && (
            <p className="mt-1 text-xs text-[#A89A88]">We'll check in with you around {prettyDate(e.checkin_date)}.</p>
          )}
          {e.note && <p className="mt-1 text-xs italic text-[#A89A88]">“{e.note}”</p>}
        </div>
        <div className="flex shrink-0 gap-1">
          <button type="button" onClick={() => setEditing(true)} data-testid={`expected-edit-${e.id}`} className="rounded-lg p-2 text-[#70584A] hover:bg-[#F5F2EC]" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
          <button type="button" onClick={dismiss} disabled={busy} data-testid={`expected-dismiss-${e.id}`} className="rounded-lg p-2 text-[#8B3A2B] hover:bg-[#FBF1EE]" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <Button disabled={busy} onClick={markReceived} data-testid={`expected-received-${e.id}`} className="mt-4 min-h-11 w-full rounded-xl border border-[#2F6B57]/40 bg-[#DDE7DF] font-semibold text-[#164C3B] hover:bg-[#d2e0d6]">
        <Check className="mr-1.5 h-4 w-4" /> It landed. Enter what arrived
      </Button>
    </div>
  );
}

export default function ExpectedIncome() {
  const { data, loading, error, reload } = useAsyncData(() => journeyApi.listExpectedIncome());
  const [adding, setAdding] = useState(false);

  if (loading) return <DashboardShell><PageHead title="Expected income" backToHome /><div className="h-28 animate-pulse rounded-2xl bg-[#F0EBE2]" /></DashboardShell>;
  if (error) return <DashboardShell><PageHead title="Expected income" backToHome /><ErrorState onRetry={reload} testid="expected-error" /></DashboardShell>;

  if (!data?.eligible) {
    return (
      <DashboardShell>
        <PageHead title="Expected income" backToHome />
        <EmptyState icon={CalendarClock} title="For variable & hybrid income" body="Expected income planning helps when money arrives on an irregular schedule, like commissions or freelance invoices. Your income is set up as predictable, so every paycheck already has a reliable rhythm." testid="expected-not-eligible" />
      </DashboardShell>
    );
  }

  const upcoming = data.upcoming || [];
  const past = data.past || [];

  return (
    <DashboardShell>
      <PageHead title="Expected income" sub="Plan for irregular money before it lands." backToHome />

      <div className="mb-5 flex items-start gap-2 rounded-2xl border border-[#C9A86A]/40 bg-[#FBF7EF] p-4" data-testid="expected-planning-note">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#9A7B3A]" />
        <p className="text-sm leading-relaxed text-[#70584A]">
          This is a plan, not money in hand. Nothing is counted and no Edit is calculated until you confirm what
          actually landed. We'll just check in with you around the dates you choose.
        </p>
      </div>

      {!adding && (
        <Button onClick={() => setAdding(true)} data-testid="expected-add-button" className="mb-5 inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#2F6B57] px-5 font-semibold text-white hover:bg-[#255643]">
          <Plus className="h-4 w-4" /> Add expected income
        </Button>
      )}
      {adding && (
        <div className="mb-6">
          <EventForm onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />
        </div>
      )}

      {upcoming.length === 0 && past.length === 0 && !adding && (
        <EmptyState icon={CalendarClock} title="Nothing planned yet" body="Add income you're expecting, like a commission or a freelance payment, and we'll help you remember to check when it should arrive." testid="expected-empty" />
      )}

      {upcoming.length > 0 && (
        <div className="space-y-3" data-testid="expected-upcoming">
          {upcoming.map((e) => <EventCard key={e.id} e={e} onChanged={reload} />)}
        </div>
      )}

      {past.length > 0 && (
        <div className="mt-8" data-testid="expected-past">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Earlier</p>
          <div className="space-y-2">
            {past.map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-xl border border-[#ECE6DC] bg-white/60 p-3.5 text-sm" data-testid={`expected-past-${e.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium text-[#171717]">{e.label}</p>
                  <p className="text-xs text-[#A89A88]">{e.amount > 0 ? `${fmt(e.amount)} · ` : ""}Expected {prettyDate(e.expected_date)}</p>
                </div>
                <span className={`ml-3 shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${e.status === "received" ? "bg-[#DDE7DF] text-[#164C3B]" : "bg-[#F0EBE2] text-[#A89A88]"}`}>
                  {e.status === "received" ? "Received" : "Dismissed"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
